import { useState } from "react";
import type { AutoTopup } from "api";
import cn from "classnames";
import { Modal } from "components/ui/Modal";
import { formatTimestamp } from "utils/date.utils";
import type { AutoTopupView, BuyMode } from "./credits.model";
import { cardLabel, formatCredits, formatUsd } from "./credits.model";

type AutoTopupCardProps = {
  view: AutoTopupView;
  /** The whole balance, for the turn-off confirmation. */
  balance: number;
  busy: boolean;
  portalBusy: boolean;
  onOpenBuy: (mode: BuyMode) => void;
  onTurnOff: () => void;
  onResume: () => void;
  onOpenPortal: () => void;
};

const TAG: Record<AutoTopupView["status"], { label: string; tone: string }> = {
  active: { label: "Active", tone: "ok" },
  failed: { label: "Payment failed", tone: "bad" },
  ending: { label: "Off", tone: "mute" },
};

const subline = (view: AutoTopupView): string => {
  if (view.status === "failed") return "Stripe will retry the payment. Update your card to avoid missing a top-up.";
  if (view.status === "ending") {
    return `Turned off. No more charges after ${formatTimestamp(view.topUp.current_period_end)}. Your QCredits stay on your balance.`;
  }

  return "Paid through Stripe. Change or turn it off at any time.";
};

const nextDate = (view: AutoTopupView): { label: string; value: string } => {
  if (view.status === "failed") {
    return {
      label: "Next retry",
      value: view.topUp.next_retry_at === null ? "No more retries" : formatTimestamp(view.topUp.next_retry_at),
    };
  }
  if (view.status === "ending") return { label: "Next charge", value: "None" };

  return { label: "Next charge", value: formatTimestamp(view.topUp.next_charge_at) };
};

/** The red banner over the balance while an auto top-up payment is failing. */
export const FailedPaymentAlert = ({
  topUp,
  portalBusy,
  onOpenPortal,
}: {
  topUp: AutoTopup;
  portalBusy: boolean;
  onOpenPortal: () => void;
}) => {
  const card = cardLabel(topUp.card) ?? "your card";
  const retry =
    topUp.next_retry_at === null
      ? "It will not try again."
      : `It will try again on ${formatTimestamp(topUp.next_retry_at)}.`;

  return (
    <div className="pcx acr__fail" role="alert">
      <div className="acr__fail-top">
        <span className="acr__fail-i" aria-hidden="true">
          !
        </span>
        <div>
          <div className="acr__fail-h">Auto top-up payment failed</div>
          <p className="acr__fail-t">
            Stripe couldn&apos;t charge {card} for {formatCredits(topUp.credits)} ({formatUsd(topUp.amount_usd)}).{" "}
            {retry} Update your card to keep your balance topped up.
          </p>
        </div>
      </div>
      <button type="button" className="pcbtn pcbtn--main acr__fail-b" disabled={portalBusy} onClick={onOpenPortal}>
        Update card
      </button>
    </div>
  );
};

/** The monthly auto top-up: what it charges, when, on which card, and its controls. */
export const AutoTopupCard = ({
  view,
  balance,
  busy,
  portalBusy,
  onOpenBuy,
  onTurnOff,
  onResume,
  onOpenPortal,
}: AutoTopupCardProps) => {
  const [confirming, setConfirming] = useState(false);
  const { topUp } = view;
  const tag = TAG[view.status];
  const next = nextDate(view);
  const card = cardLabel(topUp.card);

  return (
    <div className="pcx">
      <div className="pcx__top">
        <div>
          <div className="pcx__h">Auto top-up</div>
          <p className="pcx__sub">{subline(view)}</p>
        </div>
        <span className={cn("pc-tag", `pc-tag--${tag.tone}`)}>{tag.label}</span>
      </div>

      <dl className="acr__at-dl">
        <div>
          <dt>Amount</dt>
          <dd>
            {formatCredits(topUp.credits)} · {formatUsd(topUp.amount_usd)} / month
          </dd>
        </div>
        <div>
          <dt>{next.label}</dt>
          <dd className={cn({ "is-bad": view.status === "failed" })}>{next.value}</dd>
        </div>
        <div>
          <dt>Payment method</dt>
          <dd>{card === null ? "Via Stripe" : `${card} · via Stripe`}</dd>
        </div>
      </dl>

      {view.status === "ending" ? (
        <div className="acr__at-acts">
          <button type="button" className="pcbtn pcbtn--main" disabled={busy} onClick={onResume}>
            Resume auto top-up
          </button>
          <button type="button" className="pcbtn" onClick={() => onOpenBuy("extra")}>
            Buy QCredits
          </button>
        </div>
      ) : (
        <div className="acr__at-acts">
          {view.status === "failed" && (
            <button type="button" className="pcbtn pcbtn--main" disabled={portalBusy} onClick={onOpenPortal}>
              Update card
            </button>
          )}
          <button type="button" className="pcbtn" onClick={() => onOpenBuy("extra")}>
            Buy extra QCredits
          </button>
          <button type="button" className="pcbtn" onClick={() => onOpenBuy("change")}>
            Change amount
          </button>
          <button type="button" className="pcbtn acr__at-off" disabled={busy} onClick={() => setConfirming(true)}>
            Turn off
          </button>
        </div>
      )}

      <p className="acr__more">
        Card details and receipts are managed in the{" "}
        <button type="button" disabled={portalBusy} onClick={onOpenPortal}>
          Stripe billing portal
        </button>
        .
      </p>

      {confirming && (
        <Modal
          title="Turn off auto top-up?"
          onClose={() => setConfirming(false)}
          actions={
            <>
              <button
                type="button"
                className="pcbtn pcbtn--main"
                onClick={() => {
                  setConfirming(false);
                  onTurnOff();
                }}
              >
                Turn off
              </button>
              <button type="button" className="pcbtn" onClick={() => setConfirming(false)}>
                Keep it on
              </button>
            </>
          }
        >
          <p className="pcmodal__t">
            You won&apos;t be charged on <b>{formatTimestamp(topUp.current_period_end)}</b> or after. Your{" "}
            <b>{formatCredits(balance)}</b> stay on your balance and never expire.
          </p>
        </Modal>
      )}
    </div>
  );
};
